import { create } from "zustand";
import {
  signInWithEmailAndPassword,
  signInWithPopup,
  getAdditionalUserInfo,
  signOut,
  onAuthStateChanged,
  onIdTokenChanged,
  linkWithCredential,
  EmailAuthProvider,
  type User as FirebaseUser,
} from "firebase/auth";
import { auth, googleProvider } from "@/shared/lib/firebase";
import { apiFetchData } from "@/shared/lib/api";
import type { User } from "@/features/user/types";
import { useRestaurantStore } from "@/features/restaurants/store";

interface AuthStore {
  user: User | null;
  firebaseUser: FirebaseUser | null;
  /** Current Firebase ID token — auto-updated by onIdTokenChanged. Use synchronously. */
  token: string | null;
  isLoading: boolean;
  /** FCM device ID — session-only. Used to unregister the device token on logout. */
  deviceId: string | null;

  getToken: () => Promise<string | null>;
  /** Force-refreshes the Firebase token. Used by AuthRetry in API clients. */
  refreshTokens: () => Promise<boolean>;
  login: (email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<{ isNewUser: boolean; needsProfileCompletion: boolean }>;
  /** Links an email/password credential to the current Firebase user (Google or Phone sign-in). */
  linkEmailPassword: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setDeviceId: (id: string) => void;
  patchUser: (patch: Partial<User>) => void;
  _setFirebaseUser: (fbUser: FirebaseUser | null) => Promise<void>;
}

async function fetchUserProfile(token: string): Promise<User> {
  return apiFetchData<User>("/api/auth/me", {
    headers: { Authorization: `Bearer ${token}` },
  });
}

export const useAuthStore = create<AuthStore>()((set, get) => ({
  user: null,
  firebaseUser: null,
  token: null,
  isLoading: true,
  deviceId: null,

  async getToken() {
    const fbUser = get().firebaseUser ?? auth.currentUser;
    if (!fbUser) return null;
    return fbUser.getIdToken();
  },

  async refreshTokens() {
    const fbUser = auth.currentUser;
    if (!fbUser) return false;
    const token = await fbUser.getIdToken(true);
    set({ token });
    return true;
  },

  async login(email, password) {
    await signInWithEmailAndPassword(auth, email, password);
    // onAuthStateChanged listener handles setting state
  },

  async loginWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    // onAuthStateChanged listener handles setting state
    const info = getAdditionalUserInfo(result);
    // True if no email/password provider is linked — the reliable signal that the
    // user has never completed profile setup, regardless of isNewUser (which Firebase
    // resets to false if the account was created in a previous session then abandoned).
    const needsProfileCompletion = !result.user.providerData.some(
      (p) => p.providerId === 'password',
    );
    return { isNewUser: info?.isNewUser ?? false, needsProfileCompletion };
  },

  async linkEmailPassword(email, password) {
    const fbUser = auth.currentUser;
    if (!fbUser) throw new Error('No current user');
    const credential = EmailAuthProvider.credential(email, password);
    const result = await linkWithCredential(fbUser, credential);
    // Immediately update store so ProtectedRoute sees the new password provider
    // before navigate('/app') is called — avoids the redirect-back-to-complete-profile race.
    set({ firebaseUser: result.user });
  },

  async logout() {
    const deviceId = get().deviceId;
    if (deviceId) {
      const { deviceApi } = await import('@/features/notifications/api');
      await deviceApi.unregister(deviceId).catch(() => {});
    }
    await signOut(auth);
    set({ user: null, firebaseUser: null, token: null, deviceId: null });
    useRestaurantStore.getState().clearRestaurants();
    const { teardownPushNotifications } = await import(
      "@/features/notifications/utils/pushNotifications"
    );
    await teardownPushNotifications();
    const { queryClient } = await import("@/app/App");
    queryClient.clear();
  },

  setDeviceId(id) {
    set({ deviceId: id });
  },

  async _setFirebaseUser(fbUser) {
    if (!fbUser) {
      set({ firebaseUser: null, user: null, token: null, isLoading: false });
      return;
    }
    set({ firebaseUser: fbUser, isLoading: true });
    try {
      const token = await fbUser.getIdToken();
      const user = await fetchUserProfile(token);
      set({ token, user, isLoading: false });
    } catch {
      const token = await fbUser.getIdToken().catch(() => null);
      set({ token, isLoading: false });
    }
  },

  patchUser(patch) {
    set((prev) => (prev.user ? { user: { ...prev.user, ...patch } } : {}));
  },
}));

// Keep token in sync with Firebase — fires on sign-in, sign-out, and auto-refresh.
onIdTokenChanged(auth, async (fbUser) => {
  if (fbUser) {
    const token = await fbUser.getIdToken();
    useAuthStore.setState({ token });
  } else {
    useAuthStore.setState({ token: null });
  }
});

// Listen to Firebase auth state for the lifetime of the app.
onAuthStateChanged(auth, (fbUser) => {
  useAuthStore.getState()._setFirebaseUser(fbUser);
});
